/**
 * Timezone utilities for handling timezone conversions and formatting
 */

/**
 * Get timezone abbreviation from a timezone string (e.g., "America/New_York" -> "EST" or "EDT")
 * Always returns English abbreviations for consistency across locales.
 * @param timeZone - IANA timezone identifier (e.g., "America/New_York")
 * @param date - Date to check for DST (defaults to current date)
 */
export const getTimezoneAbbreviation = (timeZone: string, date?: Date): string => {
  try {
    const testDate = date || new Date();
    
    // Always use English for timezone abbreviations for consistency
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(testDate);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    
    return timeZonePart?.value || timeZone;
  } catch (error) {
    // Fallback to the timezone string if there's an error
    console.warn(`Failed to get timezone abbreviation for ${timeZone}:`, error);
    return timeZone;
  }
};

/**
 * Convert a Date to a specific timezone
 */
export const convertToTimezone = (date: Date, timeZone: string): Date => {
  try {
    // Create a new date with the same instant in the target timezone
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    const targetTime = new Date(utcTime + getTimezoneOffset(timeZone, date));
    return targetTime;
  } catch (error) {
    console.warn(`Failed to convert date to timezone ${timeZone}:`, error);
    return date;
  }
};

/**
 * Get the timezone offset in milliseconds for a specific timezone at a given date
 */
export const getTimezoneOffset = (timeZone: string, date: Date): number => {
  try {
    // Get the UTC time
    const utcDate = new Date(date.toISOString());
    
    // Get the time in the target timezone
    const targetDate = new Date(date.toLocaleString('en-US', { timeZone }));
    
    // Calculate the difference
    return utcDate.getTime() - targetDate.getTime();
  } catch (error) {
    console.warn(`Failed to get timezone offset for ${timeZone}:`, error);
    return 0;
  }
};

/**
 * Format a date in a specific timezone with timezone abbreviation
 * @param date - Date to format
 * @param timeZone - IANA timezone identifier
 * @param options - Intl.DateTimeFormatOptions
 * @param locale - User's locale for formatting (defaults to 'en-US')
 */
export const formatDateInTimezone = (
  date: Date, 
  timeZone: string, 
  options: Intl.DateTimeFormatOptions = {},
  locale = 'en-US'
): string => {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      ...options
    });
    
    return formatter.format(date);
  } catch (error) {
    console.warn(`Failed to format date in timezone ${timeZone} with locale ${locale}:`, error);
    return date.toLocaleString();
  }
};

/**
 * Check if a timezone is valid
 */
export const isValidTimezone = (timeZone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
};